import { useMemo, useSyncExternalStore } from 'react'
import { apiErrorMessage } from '@/lib/api-error'
import { notification } from '@/notifications/notification-store'
import { fileService } from '@/services/file.service'
import {
	cancelUpload,
	type EnqueueOptions,
	enqueue,
	enqueueAll,
	getItems,
	recover,
	removeItem,
	retryConcern,
	subscribe,
	summarize,
	type UploadItem,
	UploadItemStatus,
	type UploadRecoveryAction,
	type UploadSummary,
} from './upload-store'

const ACTIVE_STATUSES = new Set([
	UploadItemStatus.QUEUED,
	UploadItemStatus.REQUESTING_URL,
	UploadItemStatus.UPLOADING,
	UploadItemStatus.CONFIRMING,
])

/**
 * The upload queue, as a thin selector over the module store.
 *
 * The store lives outside React so an upload survives navigating away from the
 * page that started it — which is the whole point of a global queue. This hook
 * only subscribes; it owns no state of its own.
 */
export function useFileUpload(owner?: string): {
	items: UploadItem[]
	addFiles: (files: File[], options?: EnqueueOptions) => void
	enqueue: (file: File, options?: EnqueueOptions) => string
	removeItem: (id: string) => void
	retryConcern: (itemId: string, concernId: string) => Promise<void>
	/** Runs one of the actions a failed item's recovery offer carries. */
	recover: (itemId: string, action: UploadRecoveryAction) => void
	/** Cancels an upload that is still in flight or still queued. */
	cancelUpload: (itemId: string) => void
	isUploading: boolean
} {
	const all = useSyncExternalStore(subscribe, getItems, getItems)
	// Narrow to one surface's uploads when an owner is given. Two drop zones on
	// the same page share the global queue, so an unfiltered read makes each show
	// the other's progress.
	const items = useMemo(() => (owner ? all.filter((item) => item.owner === owner) : all), [all, owner])

	return {
		items,
		addFiles: (files, options) => {
			enqueueAll(files, options)
		},
		enqueue,
		removeItem,
		// Reports its own failure. Every call site fires this from an onPress and
		// discards the promise, so a 503 on retry left the user with a button that
		// visibly did nothing — the concern stayed failed with no explanation.
		// Surfaced here rather than at each site so no future caller can forget.
		retryConcern: async (itemId, concernId) => {
			try {
				await retryConcern(itemId, concernId, fileService)
			} catch (error) {
				notification.error(apiErrorMessage(error, 'Could not retry — please try again.'))
			}
		},
		// Reports its own failure for the same reason `retryConcern` does: every
		// call site fires this from an `onPress` and discards the promise, so a
		// recovery action that could not reach the server would otherwise look
		// like a button that did nothing.
		recover: (itemId, action) => {
			void recover(itemId, action).catch((error: unknown) => {
				notification.error(apiErrorMessage(error, 'Could not complete that action — please try again.'))
			})
		},
		cancelUpload,
		// Only the pre-confirm span counts as "uploading" — once the bytes are in,
		// the remaining work is the server's concerns, tracked per item instead.
		isUploading: items.some((item) => ACTIVE_STATUSES.has(item.status)),
	}
}

/**
 * `{ total, settled }` for the status-bar chip — a second selector over the same
 * store, so the chip and the queue can never disagree about what is outstanding.
 */
export function useUploadSummary(): UploadSummary {
	// Subscribes to `items` (a stable reference between mutations) and derives the
	// counts here. Passing `getSummary` to `useSyncExternalStore` directly would
	// return a fresh object on every equality check and loop forever.
	const items = useSyncExternalStore(subscribe, getItems, getItems)
	return summarize(items)
}
